import { FC, useCallback, useMemo, useState } from "react";
import { EditProgram, Media, MediaFolder, Program } from "../../../../types";
import { Button } from "../../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../Grid";
import {
  Input,
  Label,
  Textarea,
  Radio,
  Checkbox,
} from "../../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { useStores } from "../../../../hooks/useStores";
import { Select } from "../../Select";
import i18n from "../../../../i18n";
import Feedback from "../../../UI/Forms/FormElements/Feedback";
import { observer } from "mobx-react";
import { DatePicker } from "../../DatePicker";
import { FileUploadModal } from "../../FileUploadModal";
import { useDisclosure } from "react-use-disclosure";
import Creatable from "react-select/creatable";
import { CircularProgress } from "@mui/material";
import { VimeoMediaFolderSelector } from "../../../ScreenProgramDetail/VideoForm";
import { AsyncPaginate } from "react-select-async-paginate";
import { useLoadVimeoVideoOptions } from "../../../../hooks/vimeoFoldersAndVideos/useLoadVimeoVideoOptions";

type Props = {
  program?: Program;
  isSubmitting: boolean;
  onSubmit: (values: EditProgram) => void;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
  medias: Media[];
  onCancel: () => void;
  onChangeFolder: (folderId: MediaFolder["projectId"]) => void;
};

const ProgramForm: FC<Props> = observer(
  ({
    program,
    isSubmitting,
    medias,
    onSubmit,
    editable = true,
    isAdmin = false,
    validable,
    onCancel,
    onChangeFolder,
  }) => {
    const {
      categoryStore,
      bannerStore,
      adCircuitStore,
      programStore,
      userStore,
    } = useStores();
    const {
      isOpen: isModalUploadOpen,
      open: openModalUpload,
      close: closeModalUpload,
    } = useDisclosure();
    const [selectedFolder, setSelectedFolder] = useState<{
      label: string;
      value: string;
    }>(
      {} as {
        label: string;
        value: string;
      }
    );

    const { t } = useTranslation();
    const formik = useFormik({
      initialValues: {
        id: program?.id || null,
        title: program?.title || "",
        description: program?.description || "",
        url: "",
        posterUrl: program?.posterUrl || "",
        numberOfSeasons: program?.numberOfSeasons || 1,
        categories:
          (program &&
            program.categories &&
            program.categories.map(
              (category: any) => category.category.id
              // .toString()
            )) ||
          [],
        bannerId:
          (program?.banners &&
            program?.banners[0] &&
            program?.banners[0].bannerId) ||
          null,
        adCircuitId:
          (program?.ads && program?.ads[0] && program?.ads[0].adCircuitId) ||
          null,
        validFrom: program?.validFrom
          ? new Date(program?.validFrom)
          : undefined,
        validUntil: program?.validUntil
          ? new Date(program?.validUntil)
          : undefined,
        typology: program?.typology || "Movie",
        mustSee: program?.mustSee || false,
        tags: program?.tags || [],
        isVipOnly: program?.isVipOnly || false,
        trailerId: program?.trailerId || null,
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        title: Yup.string().required(
          t("components.common.programForm.titleRequired")
        ),
        posterUrl: Yup.string().required(
          t("components.common.programForm.posterUrlRequired")
        ),
        categories: Yup.array().min(
          1,
          t("components.common.programForm.categoriesRequired")
        ),
        numberOfSeasons: Yup.number().when(["typology"], (typology, schema) => {
          return typology === "TVShow"
            ? schema.min(
                1,
                t("components.common.programForm.numberOfSeasonRequired")
              )
            : schema;
        }),
        validFrom: Yup.date()
          .default(() => new Date())
          .required(t("components.common.programForm.validFromRequired")),
        validUntil: Yup.date().required(
          t("components.common.programForm.validUntilRequired")
        ),
      }),
    });

    const categoryOptions = useMemo(() => {
      return categoryStore.programCategories.map((programCategory) => ({
        label: programCategory.labels[i18n.language],
        value: programCategory.id,
      }));
    }, [categoryStore.programCategories]);

    const bannerOptions = useMemo(() => {
      return [
        { label: "---", value: null },
        ...bannerStore.banners.map((banner) => ({
          label: banner.title,
          value: banner.id,
        })),
      ];
    }, [bannerStore.banners]);

    const adCircuitOptions = useMemo(() => {
      return [
        { label: "---", value: null },
        ...adCircuitStore.adCircuits.map((adCircuit) => ({
          label: adCircuit.title,
          value: adCircuit.id,
        })),
      ];
    }, [adCircuitStore.adCircuits]);

    const getSelectedBannerId: any = useCallback(
      (id: any) => {
        return {
          label: bannerStore.banners.find((banner) => banner.id === id)?.title,
          value: id,
        };
      },
      [bannerStore.banners]
    );

    const getSelectedAdCircuitId: any = useCallback(
      (id: any) => {
        return {
          label: adCircuitStore.adCircuits.find(
            (adCircuit) => adCircuit.id === id
          )?.title,
          value: id,
        };
      },
      [adCircuitStore.adCircuits]
    );

    const handleUploadPoster = useCallback(
      async (imageUrls: string[]) => {
        formik.setFieldValue("posterUrl", imageUrls[0]);
        closeModalUpload();
      },
      [formik, closeModalUpload]
    );

    const tagsOptions = useMemo(() => {
      return program && program.tags
        ? program?.tags.map((tag, index) => ({
            label: tag,
            value: index,
          }))
        : [];
    }, [program]);

    const { loadVimeoVideoOptions, getSelectedVideoId } =
      useLoadVimeoVideoOptions({ programStore, selectedFolder, medias });

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.common.programForm.title")}
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder={t(
                    "components.common.programForm.titlePlaceholder"
                  )}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  state={formik.errors.title ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("title").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="description">
                  {t("components.common.programForm.description")}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t(
                    "components.common.programForm.descriptionPlaceholder"
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  showState={true}
                />
              </FormControl>
            </Col>
          </Row>
          {/* <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="url">
                  {t("components.common.programForm.url")}
                </Label>
                <Input
                  id="url"
                  name="url"
                  placeholder={t(
                    "components.common.programForm.urlPlaceholder"
                  )}
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  state={formik.errors.url ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("url").error}
                />
              </FormControl>
            </Col>
          </Row> */}
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="posterUrl">
                  {t("components.common.programForm.posterUrl")}
                </Label>
                {formik.values.posterUrl.length > 0 ? (
                  <img
                    src={formik.values.posterUrl}
                    alt={formik.values.posterUrl}
                  />
                ) : (
                  <p>
                    {t("components.common.programForm.posterUrlPlaceholder")}
                  </p>
                )}
                <Button color="light" onClick={openModalUpload}>
                  {t("common.upload")}
                </Button>
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.common.programForm.trailerUrl")}
                </Label>
                <Input
                    id="trailerId"
                    name="trailerId"
                    placeholder={t(
                        "components.common.programForm.trailer"
                    )}
                    value={formik.values.trailerId ?? ""}
                    onChange={formik.handleChange}
                    state={formik.errors.trailerId ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("trailerId").error}
                />
              </FormControl>
            </Col>
          </Row>
          {/*<VimeoMediaFolderSelector*/}
          {/*  programStore={programStore}*/}
          {/*  userStore={userStore}*/}
          {/*  t={t}*/}
          {/*  onChangeFolder={onChangeFolder}*/}
          {/*  selectedFolder={selectedFolder}*/}
          {/*  setSelectedFolder={setSelectedFolder}*/}
          {/*  customLabel={t("components.common.programForm.trailerInfo")}*/}
          {/*/>*/}
          {/*<Row mt="10px">*/}
          {/*  <Col noGutter>*/}
          {/*    <FormControl fullWidth>*/}
          {/*      <Label htmlFor="trailerId">*/}
          {/*        {t("components.common.programForm.trailer")}*/}
          {/*      </Label>*/}
          {/*      <AsyncPaginate*/}
          {/*        key={programStore.mediaListTotal}*/}
          {/*        id={"trailerId"}*/}
          {/*        name={"trailerId"}*/}
          {/*        value={getSelectedVideoId(formik.values.trailerId)}*/}
          {/*        loadOptions={loadVimeoVideoOptions}*/}
          {/*        onChange={(option: any) => {*/}
          {/*          formik.setFieldValue("trailerId", option?.value || null);*/}
          {/*        }}*/}
          {/*        additional={{*/}
          {/*          page: 1,*/}
          {/*        }}*/}
          {/*        loadingMessage={() => t("common.loading")}*/}
          {/*        isDisabled={!selectedFolder.value || medias.length === 0}*/}
          {/*      />*/}
          {/*      {programStore.isLoading && (*/}
          {/*        <Feedback*/}
          {/*          state={"warning"}*/}
          {/*          showState={true}*/}
          {/*          showErrorOnly={false}*/}
          {/*        >*/}
          {/*          {t("common.loading")}*/}
          {/*        </Feedback>*/}
          {/*      )}*/}
          {/*      {formik.errors.trailerId && (*/}
          {/*        <Feedback state="error" showState={true} showErrorOnly={true}>*/}
          {/*          {formik.getFieldMeta("trailerId").error}*/}
          {/*        </Feedback>*/}
          {/*      )}*/}
          {/*    </FormControl>*/}
          {/*  </Col>*/}
          {/*</Row>*/}
          <Row paddingTop="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="typology">
                  {t("components.common.programForm.typology")}
                </Label>
                <Radio
                  id="typology"
                  name="typology"
                  value={formik.values.typology}
                  label={t("components.common.programForm.movie")}
                  onChange={() => {
                    formik.setFieldValue("typology", "Movie");
                    formik.setFieldValue("numberOfSeasons", undefined);
                  }}
                  checked={formik.values.typology === "Movie"}
                />
                <Radio
                  id="typology2"
                  name="typology"
                  value={formik.values.typology}
                  label={t("components.common.programForm.tvShow")}
                  onChange={() => {
                    formik.setFieldValue("typology", "TVShow");
                    formik.setFieldValue("numberOfSeasons", 1);
                  }}
                  checked={formik.values.typology === "TVShow"}
                />

                {formik.errors.typology && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("typology").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="bannerId">
                  {t("components.common.programForm.bannerId")}
                </Label>
                <Select
                  id="bannerId"
                  name="bannerId"
                  options={bannerOptions}
                  value={getSelectedBannerId(formik.values.bannerId)}
                  onChange={(option: any) => {
                    formik.setFieldValue("bannerId", option?.value || null);
                  }}
                />
                {formik.errors.bannerId && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("bannerId").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="adCircuitId">
                  {t("components.common.programForm.adCircuitId")}
                </Label>
                <Select
                  id="adCircuitId"
                  name="adCircuitId"
                  options={adCircuitOptions}
                  value={getSelectedAdCircuitId(formik.values.adCircuitId)}
                  onChange={(option: any) => {
                    formik.setFieldValue("adCircuitId", option?.value || null);
                  }}
                />
                {formik.errors.adCircuitId && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("adCircuitId").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="categories">
                  {t("components.common.programForm.categories")}
                </Label>
                <Select
                  id="categories"
                  name="categories"
                  options={categoryOptions}
                  // @ts-ignore
                  isMulti
                  value={formik.values.categories?.map((categoryID) => ({
                    label: categoryStore.programCategories.find(
                      (category) =>
                        category.id?.toString() === categoryID.toString()
                    )?.labels[i18n.language],
                    value: categoryID,
                  }))}
                  onChange={(options: any) => {
                    const selectedOptions = options as any;
                    formik.setFieldValue(
                      "categories",
                      options
                        ? (
                            selectedOptions as {
                              label: string;
                              value: string;
                            }[]
                          ).map((option) => option.value.toString())
                        : []
                    );
                  }}
                />
                {formik.errors.categories && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("categories").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validFrom">
                  {t("components.common.programForm.validFrom")}
                </Label>

                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.common.programForm.validFromPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validFrom || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validFrom", date || null);
                    formik.setFieldValue("validUntil", new Date("2099-12-31"));
                  }}
                />
                {formik.errors.validFrom && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validFrom").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validUntil">
                  {t("components.common.programForm.validUntil")}
                </Label>
                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.common.programForm.validUntilPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validUntil || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validUntil", date || null);
                  }}
                />
                {formik.errors.validUntil && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validUntil").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row paddingTop="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="mustSee">
                  {t("components.common.programForm.mustSee")}
                </Label>
                <Checkbox
                  id="mustSee"
                  name="mustSee"
                  value={formik.values.mustSee.toString()}
                  label={""}
                  onChange={formik.handleChange}
                  checked={formik.values.mustSee}
                />

                {formik.errors.mustSee && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("mustSee").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="tags">
                  {t("components.common.programForm.tags")}
                </Label>
                <Creatable
                  id="tags"
                  name="tags"
                  options={tagsOptions}
                  isMulti
                  placeholder={t(
                    "components.common.programForm.tagsPlaceholder"
                  )}
                  value={formik.values.tags?.map((tag, index) => ({
                    label: tag,
                    value: index,
                  }))}
                  onChange={(options: any) => {
                    const selectedOptions = options as any;
                    formik.setFieldValue(
                      "tags",
                      options
                        ? (
                            selectedOptions as {
                              label: string;
                              value: string;
                            }[]
                          ).map((option) => option.label)
                        : []
                    );
                  }}
                />
              </FormControl>
            </Col>
          </Row>
          <Row paddingTop="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="isVipOnly">
                  {t("components.common.programForm.isVipOnly")}
                </Label>
                <Checkbox
                  id="isVipOnly"
                  name="isVipOnly"
                  value={formik.values.isVipOnly.toString()}
                  label={""}
                  onChange={formik.handleChange}
                  checked={formik.values.isVipOnly}
                />
              </FormControl>
            </Col>
          </Row>
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"} disabled={isSubmitting}>
              {t("common.save")}
              {isSubmitting && (
                <>
                  {" "}
                  <CircularProgress color={"info"} size={25} />
                </>
              )}
            </Button>
          </Col>
        </Row>

        <FileUploadModal
          isOpen={isModalUploadOpen}
          onClose={closeModalUpload}
          onFileUploaded={handleUploadPoster}
          maxFiles={1}
          accept="image/jpeg, image/png"
        />
      </form>
    );
  }
);

export default ProgramForm;
